import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import html2canvas from "html2canvas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowLeft, CheckCircle2, Copy, Save, Send, Sparkles, Target, Trophy, Wand2,
} from "lucide-react";
import { ScoreBadge, Stat, SimpleMd } from "@/components/app/ContentStudio.parts";
import RichEditor from "@/components/app/content-studio/RichEditor";
import PublishMenu from "@/components/app/content-studio/PublishMenu";

/**
 * Full draft editor with autoscoring, AI writer, share card and bulk
 * section generation. Self-contained — owns all editor-only state.
 *
 * Props:
 *  - projectId
 *  - initial: { id?, title?, target_keyword?, content?, autoGenerateSections? }
 *      `autoGenerateSections` is an array of H2 headings to auto-fill on mount
 *      (used by Magic Mode in the drafts tab).
 *  - onBack()         user clicked "Back to drafts"
 *  - onSaved(draft)   draft was created/updated — parent should refresh list
 */
export default function DraftEditor({ projectId, initial, onBack, onSaved }) {
  const [current, setCurrent] = useState(initial?.id ? initial : null);
  const [title, setTitle] = useState(initial?.title || "");
  const [keyword, setKeyword] = useState(initial?.target_keyword || "");
  const [content, setContent] = useState(initial?.content || "");
  const [score, setScore] = useState(null);
  const [aiFeedback, setAiFeedback] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [tone, setTone] = useState("professional");
  const [length, setLength] = useState("medium");
  const [topic, setTopic] = useState("");
  const [goal, setGoal] = useState(90);
  const [autoScoring, setAutoScoring] = useState(false);
  const [bulkGen, setBulkGen] = useState({ active: false, done: 0, total: 0 });
  const [publishOpen, setPublishOpen] = useState(false);
  const cardRef = useRef(null);

  const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;
  const kwHits = keyword.trim()
    ? (content.toLowerCase().match(new RegExp(keyword.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g")) || []).length
    : 0;

  // ----- Section autogen helpers --------------------------------------
  const generateSection = async (heading) => {
    try {
      const { data } = await api.post(`/projects/${projectId}/content/section`, {
        heading, target_keyword: keyword, context: content,
      });
      const block = `## ${heading}\n\n[autogen]`;
      const filled = `## ${heading}\n\n${data.content.trim()}`;
      setContent((c) => c.replace(block, filled));
      toast.success("Section generated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const generateAllSections = async () => {
    const matches = [...content.matchAll(/##\s+([^\n]+)\n\n\[autogen\]/g)];
    if (matches.length === 0) { toast.error("No pending sections"); return; }
    if (!keyword.trim()) { toast.error("Set a target keyword first"); return; }
    const headings = matches.map((m) => m[1]);
    setBulkGen({ active: true, done: 0, total: headings.length });
    const ctx = content;
    await Promise.all(headings.map(async (heading) => {
      try {
        const { data } = await api.post(`/projects/${projectId}/content/section`, {
          heading, target_keyword: keyword, context: ctx,
        });
        const block = `## ${heading}\n\n[autogen]`;
        const filled = `## ${heading}\n\n${data.content.trim()}`;
        setContent((c) => c.replace(block, filled));
        setBulkGen((b) => ({ ...b, done: b.done + 1 }));
      } catch {
        setBulkGen((b) => ({ ...b, done: b.done + 1 }));
      }
    }));
    setBulkGen({ active: false, done: 0, total: 0 });
    toast.success(`Generated ${headings.length} sections`);
  };

  // ----- Magic Mode: auto-generate seeded sections once on mount ------
  const autoSections = initial?.autoGenerateSections;
  useEffect(() => {
    if (!autoSections || autoSections.length === 0) return;
    const kw = initial?.target_keyword || keyword;
    if (!kw) return;
    const skeleton = initial?.content || "";
    setBulkGen({ active: true, done: 0, total: autoSections.length });
    (async () => {
      await Promise.all(autoSections.map(async (heading) => {
        try {
          const { data } = await api.post(`/projects/${projectId}/content/section`, {
            heading, target_keyword: kw, context: skeleton,
          });
          const block = `## ${heading}\n\n[autogen]`;
          const filled = `## ${heading}\n\n${data.content.trim()}`;
          setContent((c) => c.replace(block, filled));
        } catch { /* skip */ }
        setBulkGen((b) => ({ ...b, done: b.done + 1 }));
      }));
      setBulkGen({ active: false, done: 0, total: 0 });
    })();
  }, []);

  // ----- Analyze (manual + AI) ----------------------------------------
  const analyzeEditor = async (useAi = false) => {
    if (!keyword.trim()) { toast.error("Add a target keyword first"); return; }
    if (!content.trim()) { toast.error("Write or paste some content first"); return; }
    setAnalyzing(true); setAiFeedback("");
    try {
      const { data } = await api.post(`/projects/${projectId}/content/analyze`, {
        target_keyword: keyword, content, use_ai: useAi,
      });
      setScore(data.score);
      if (data.ai_feedback) setAiFeedback(data.ai_feedback);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    setAnalyzing(false);
  };

  // Debounced autoscore — fires when keyword/content change.
  useEffect(() => {
    if (!keyword.trim() || !content.trim()) return;
    setAutoScoring(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(`/projects/${projectId}/content/analyze`, {
          target_keyword: keyword, content, use_ai: false,
        });
        setScore(data.score);
      } catch { /* silent */ }
      setAutoScoring(false);
    }, 900);
    return () => { clearTimeout(t); setAutoScoring(false); };
  }, [keyword, content, projectId]);

  // ----- AI writer -----------------------------------------------------
  const generateDraft = async () => {
    if (!topic.trim() || !keyword.trim()) {
      toast.error("Topic and target keyword are required");
      return;
    }
    setDrafting(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/content/draft`, {
        topic, target_keyword: keyword, tone, length,
      });
      setContent(data.content);
      if (!title) setTitle(topic);
      toast.success("Draft generated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    setDrafting(false);
  };

  // ----- Save / share --------------------------------------------------
  const save = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (current?.id) {
        const { data } = await api.patch(`/projects/${projectId}/content/drafts/${current.id}`, {
          title, target_keyword: keyword, content,
        });
        setCurrent(data);
        toast.success("Draft updated");
        onSaved?.(data);
      } else {
        const { data } = await api.post(`/projects/${projectId}/content/drafts`, {
          title, target_keyword: keyword, content,
        });
        setCurrent(data);
        toast.success("Draft saved");
        onSaved?.(data);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    setSaving(false);
  };

  const exportShareCard = async () => {
    if (!cardRef.current) return;
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#ffffff", scale: 2, useCORS: true, logging: false,
      });
      const link = document.createElement("a");
      link.download = `arloseo-${(title || "draft").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Share card downloaded");
    } catch { toast.error("Export failed"); }
  };

  // -------- Render -----------------------------------------------------
  const pendingSections = [...content.matchAll(/##\s+([^\n]+)\n\n\[autogen\]/g)];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button onClick={onBack} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="editor-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to drafts
        </button>
        <div className="flex items-center gap-2">
          <span className="font-mono-data text-xs text-muted-foreground">{wordCount} words · {kwHits} keyword hits</span>
          <Button size="sm" variant="outline" onClick={()=>{ navigator.clipboard.writeText(content); toast.success("Copied to clipboard"); }} className="rounded-none h-9" data-testid="editor-copy">
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
          </Button>
          <Button size="sm" variant="outline" onClick={()=>setPublishOpen(true)} className="rounded-none h-9" data-testid="editor-publish">
            <Send className="w-3.5 h-3.5 mr-1.5" /> Publish
          </Button>
          <Button size="sm" onClick={save} disabled={saving} className="rounded-none h-9 bg-foreground hover:bg-foreground/90" data-testid="editor-save">
            <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <PublishMenu
        open={publishOpen}
        onOpenChange={setPublishOpen}
        projectId={projectId}
        draftId={current?.id || null}
        title={title}
        markdown={content}
      />

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-border bg-white p-4 space-y-3">
          <Input value={title} onChange={(e)=>setTitle(e.target.value)} placeholder="Article title"
            className="rounded-none h-11 border-foreground/30 focus-visible:ring-foreground font-display text-lg"
            data-testid="editor-title" />
          <Input value={keyword} onChange={(e)=>setKeyword(e.target.value)} placeholder="Target keyword (e.g. 'best running shoes')"
            className="rounded-none h-10 border-foreground/30 focus-visible:ring-foreground"
            data-testid="editor-keyword" />
          <RichEditor
            value={content}
            onChange={setContent}
            projectId={projectId}
            targetKeyword={keyword}
            testId="editor-rich"
            surfaceTestId="editor-content"
          />

          {pendingSections.length > 0 && (
            <div className="border border-dashed border-foreground/30 p-3">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <p className="font-mono-data text-[11px] uppercase tracking-widest text-muted-foreground">
                  {bulkGen.active ? `Generating ${bulkGen.done}/${bulkGen.total}…` : `${pendingSections.length} section(s) pending`}
                </p>
                <Button size="sm" onClick={generateAllSections} disabled={bulkGen.active} className="rounded-none h-8 bg-foreground hover:bg-foreground/90" data-testid="gen-all-sections">
                  <Sparkles className="w-3 h-3 mr-1.5" /> {bulkGen.active ? "Working…" : "Generate all"}
                </Button>
              </div>
              {bulkGen.active && (
                <Progress value={bulkGen.total ? (bulkGen.done / bulkGen.total) * 100 : 0} className="h-1 mb-2 rounded-none [&>div]:bg-foreground" />
              )}
              <div className="flex flex-wrap gap-2">
                {pendingSections.map((m, i) => (
                  <Button key={i} size="sm" variant="outline" onClick={()=>generateSection(m[1])} disabled={bulkGen.active} className="rounded-none h-8" data-testid={`gen-section-${i}`}>
                    <Sparkles className="w-3 h-3 mr-1.5" /> {m[1].slice(0, 40)}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button onClick={()=>analyzeEditor(false)} disabled={analyzing} variant="outline" className="rounded-none h-9" data-testid="editor-analyze">
              {analyzing ? "Analyzing…" : "Run SEO check"}
            </Button>
            <Button onClick={()=>analyzeEditor(true)} disabled={analyzing} className="rounded-none h-9 bg-foreground hover:bg-foreground/90" data-testid="editor-analyze-ai">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> {analyzing ? "Thinking…" : "Analyze + AI feedback"}
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {/* Gamified Score Goal */}
          <div className={`border p-4 transition-colors ${score && score.score >= goal ? "border-emerald-400 bg-emerald-50" : "border-border bg-white"}`} data-testid="score-goal-panel">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {score && score.score >= goal
                  ? <Trophy className="w-4 h-4 text-emerald-600" />
                  : <Target className="w-4 h-4" />}
                <h4 className="font-display tracking-tight text-base">Score Goal</h4>
              </div>
              <div className="flex items-center gap-1 font-mono-data text-xs">
                {[70, 80, 90].map((g) => (
                  <button key={g}
                    onClick={() => setGoal(g)}
                    className={`px-2 py-0.5 border ${goal===g ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"}`}
                    data-testid={`score-goal-${g}`}
                  >{g}</button>
                ))}
              </div>
            </div>
            <div className="mt-3">
              <div className="flex items-baseline justify-between">
                <div>
                  <span className="font-semibold text-4xl tracking-tight">{score ? score.score : 0}</span>
                  <span className="text-muted-foreground font-mono-data text-sm ml-1">/ {goal}</span>
                </div>
                <span className="font-mono-data text-[11px] uppercase tracking-widest text-muted-foreground">
                  {autoScoring ? "scoring…" : (score && score.score >= goal ? "goal hit" : "in progress")}
                </span>
              </div>
              <Progress
                value={Math.min(100, score ? (score.score / goal) * 100 : 0)}
                className="h-2 mt-2 rounded-none [&>div]:bg-foreground"
              />
            </div>
            {score && (
              <div className="mt-4">
                <p className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Quest log</p>
                <ul className="space-y-1.5 text-xs">
                  {[
                    [score.in_title, "Target keyword in title"],
                    [score.in_h1, "Target keyword in H1"],
                    [score.in_first_100, "Target keyword in first 100 words"],
                    [score.keyword_density >= 0.5 && score.keyword_density <= 3.5, `Keyword density 0.5–3.5% (now ${score.keyword_density}%)`],
                    [score.h2.length >= 3, `At least 3 H2 sections (now ${score.h2.length})`],
                    [score.word_count >= 1200, `1,200+ words (now ${score.word_count})`],
                    [score.flesch >= 50, `Readability ≥ 50 (now ${score.flesch})`],
                    [score.avg_sentence_length > 0 && score.avg_sentence_length <= 25, `Avg sentence ≤ 25 words (now ${score.avg_sentence_length})`],
                  ].map(([done, label], i) => (
                    <li key={i} className={`flex items-start gap-2 ${done ? "text-emerald-700" : "text-muted-foreground"}`} data-testid={`quest-item-${i}`}>
                      <span className={`w-4 h-4 inline-flex items-center justify-center text-[10px] shrink-0 ${done ? "bg-emerald-600 text-white" : "border border-muted-foreground/30"}`}>
                        {done ? "✓" : ""}
                      </span>
                      <span>{label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {score && score.score >= goal && (
              <div className="mt-4 border border-emerald-300 bg-white p-2 font-mono-data text-[11px] uppercase tracking-widest text-emerald-700 text-center">
                ★ Goal reached — ship it ★
              </div>
            )}
          </div>

          {score && score.score >= goal && (
            <div className="border border-border bg-white p-4" data-testid="share-card-panel">
              <div className="flex items-center justify-between">
                <h4 className="font-display tracking-tight text-base flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" /> Share your win</h4>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => {
                    const txt = `Just shipped a ${score.score}/100 SEO score on "${title || 'my latest draft'}" with @arloseo 🚀\n\nTry the free audit:`;
                    const url = `${window.location.origin}/free-audit?utm_source=twitter&utm_medium=share_card&utm_campaign=score_win`;
                    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(txt)}&url=${encodeURIComponent(url)}`, "_blank");
                    if (current?.id) {
                      api.post(`/projects/${projectId}/content/drafts/${current.id}/publish/log`, {
                        kind: "twitter", url, status: `${score.score}/100`,
                      }).catch(() => {});
                    }
                  }} className="rounded-none h-8" data-testid="share-card-tweet">
                    <Sparkles className="w-3 h-3 mr-1.5" /> Tweet
                  </Button>
                  <Button size="sm" onClick={exportShareCard} className="rounded-none h-8 bg-foreground hover:bg-foreground/90" data-testid="share-card-export">
                    <Copy className="w-3 h-3 mr-1.5" /> Export PNG
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Download a tweet-ready card and brag online.</p>
              <div ref={cardRef} className="mt-3 border border-foreground bg-foreground text-background p-6 relative overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />
                <div className="relative">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold tracking-tight text-lg">ARLOSEO</span>
                    <span className="font-mono-data text-[10px] uppercase tracking-widest text-background/60">/score-card</span>
                  </div>
                  <p className="font-mono-data text-[10px] uppercase tracking-widest text-background/60 mt-6">// shipped at</p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="font-semibold text-7xl tracking-tight text-emerald-400">{score.score}</span>
                    <span className="text-background/60 font-mono-data text-sm">/ 100</span>
                  </div>
                  <h5 className="font-semibold text-xl tracking-tight mt-4 line-clamp-2">{title || "Untitled draft"}</h5>
                  <p className="font-mono-data text-xs text-background/60 mt-1">target · {keyword || "—"}</p>
                  <div className="grid grid-cols-3 gap-px bg-background/10 mt-4">
                    <div className="bg-foreground p-2"><div className="font-mono-data text-[10px] uppercase text-background/60">Words</div><div className="font-semibold text-lg">{score.word_count}</div></div>
                    <div className="bg-foreground p-2"><div className="font-mono-data text-[10px] uppercase text-background/60">Read</div><div className="font-semibold text-lg">{score.reading_minutes}m</div></div>
                    <div className="bg-foreground p-2"><div className="font-mono-data text-[10px] uppercase text-background/60">Flesch</div><div className="font-semibold text-lg">{score.flesch}</div></div>
                  </div>
                  <p className="font-mono-data text-[10px] uppercase tracking-widest text-background/40 mt-5">arloseo.app · ai-powered seo workspace</p>
                </div>
              </div>
            </div>
          )}

          <div className="border border-border bg-white p-4">
            <div className="flex items-center justify-between">
              <h4 className="font-display tracking-tight text-base flex items-center gap-2"><Wand2 className="w-4 h-4" /> AI Writer</h4>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Generates a full Markdown draft via Claude.</p>
            <div className="space-y-2 mt-3">
              <Input value={topic} onChange={(e)=>setTopic(e.target.value)} placeholder="Topic / angle"
                className="rounded-none h-10 border-foreground/30 focus-visible:ring-foreground"
                data-testid="writer-topic" />
              <div className="grid grid-cols-2 gap-2">
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger className="rounded-none h-10 border-foreground/30" data-testid="writer-tone"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="casual">Casual</SelectItem>
                    <SelectItem value="authoritative">Authoritative</SelectItem>
                    <SelectItem value="playful">Playful</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={length} onValueChange={setLength}>
                  <SelectTrigger className="rounded-none h-10 border-foreground/30" data-testid="writer-length"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="short">Short (~700)</SelectItem>
                    <SelectItem value="medium">Medium (~1200)</SelectItem>
                    <SelectItem value="long">Long (~2000)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={generateDraft} disabled={drafting} className="w-full rounded-none h-10 bg-foreground hover:bg-foreground/90" data-testid="writer-generate">
                {drafting ? "Writing…" : <><Sparkles className="w-4 h-4 mr-1.5" /> Generate draft</>}
              </Button>
              {content && <p className="text-[11px] text-muted-foreground">Generating again will replace current content.</p>}
            </div>
          </div>

          {score && (
            <div className="border border-border bg-white p-4">
              <div className="flex items-center justify-between">
                <h4 className="font-display tracking-tight text-base">SEO Score</h4>
                <ScoreBadge score={score.score} />
              </div>
              <div className="grid grid-cols-2 -mr-px -mb-px mt-3 text-xs">
                <Stat label="Words" value={score.word_count} />
                <Stat label="Read time" value={`${score.reading_minutes}m`} />
                <Stat label="Density" value={`${score.keyword_density}%`} />
                <Stat label="Hits" value={score.keyword_count} />
                <Stat label="Flesch" value={score.flesch} />
                <Stat label="H2s" value={score.h2.length} />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3 text-[11px] font-mono-data">
                {[
                  ["TITLE", score.in_title],
                  ["H1", score.in_h1],
                  ["H2", score.in_h2],
                  ["FIRST 100", score.in_first_100],
                ].map(([l,v]) => (
                  <div key={l} className={`border px-2 py-1 ${v ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-amber-300 text-amber-700 bg-amber-50"}`}>
                    {v ? "✓" : "✗"} kw in {l}
                  </div>
                ))}
              </div>
              <div className="mt-4 space-y-2 text-xs">
                {score.issues.map((it,i)=>(<div key={`i${i}`} className="flex gap-2"><AlertTriangle className="w-3.5 h-3.5 text-red-600 shrink-0 mt-0.5" /><span>{it}</span></div>))}
                {score.warnings.map((it,i)=>(<div key={`w${i}`} className="flex gap-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" /><span>{it}</span></div>))}
                {score.passed.slice(0,3).map((it,i)=>(<div key={`p${i}`} className="flex gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" /><span>{it}</span></div>))}
              </div>
            </div>
          )}

          {aiFeedback && (
            <div className="border border-border bg-white p-4">
              <h4 className="font-display tracking-tight text-base flex items-center gap-2"><Sparkles className="w-4 h-4" /> AI editor</h4>
              <div className="mt-3"><SimpleMd text={aiFeedback} /></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
